import { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui';

const API = (process.env.REACT_APP_API_URL || '/api/v1') + '/pages/media-slots';
const MEDIA_API = (process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001') + '/api/media';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('mh_token')}`,
  'Content-Type': 'application/json',
});

const mediaAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('mh_token')}`,
});

const TYPE_ICON  = { video: '▶', image: '◈', text: '◻' };
const TYPE_COLOR = { video: 'var(--blue-light)', image: 'var(--accent)', text: 'var(--green-light)' };

function formatSize(b) {
  if (!b) return '';
  if (b < 1024*1024) return `${(b/1024).toFixed(0)} KB`;
  return `${(b/1024/1024).toFixed(1)} MB`;
}

// ── Media picker modal ────────────────────────────────────
function MediaPicker({ type, onPick, onClose }) {
  const [assets, setAssets]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [page, setPage]       = useState(1);
  const [pagination, setPag]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 20, page });
      if (type === 'video') params.set('type', 'video');
      if (type === 'image') params.set('type', 'image');
      if (search) params.set('search', search);
      const res = await fetch(`${MEDIA_API}?${params}`, { headers: mediaAuthHeaders() });
      const data = await res.json();
      setAssets(data.data || []);
      setPag(data.pagination);
    } catch { setAssets([]); } finally { setLoading(false); }
  }, [type, search, page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', width: 720, maxWidth: '95vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', animation: 'fadeIn 0.2s ease' }}>

        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>
            Pick {type === 'video' ? 'a Video' : type === 'image' ? 'an Image' : 'a File'}
          </span>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <input
              placeholder="Search…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '7px 12px', fontSize: 12, width: 180 }}
            />
            <button onClick={onClose} style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}>✕</button>
          </div>
        </div>

        {/* Grid */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>Loading…</div>
          ) : assets.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.3 }}>{TYPE_ICON[type] || '◻'}</div>
              <div>No {type} files found</div>
              <div style={{ fontSize: 11, marginTop: 6, color: 'var(--text-muted)' }}>Upload files in the Media Library first</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10 }}>
              {assets.map(a => {
                const thumb = a.thumbnail_url || (a.type === 'image' ? a.file_url : null);
                return (
                  <div key={a.id} onClick={() => onPick(a)}
                    style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden', cursor: 'pointer', background: 'var(--bg-elevated)', transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
                  >
                    <div style={{ height: 90, background: 'var(--bg-surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
                      {thumb
                        ? <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} loading="lazy" />
                        : <span style={{ fontSize: 24, color: TYPE_COLOR[a.type], opacity: 0.5 }}>{TYPE_ICON[a.type]}</span>
                      }
                      {a.type === 'video' && (
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.3)' }}>
                          <span style={{ fontSize: 18, color: '#fff' }}>▶</span>
                        </div>
                      )}
                    </div>
                    <div style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: 10, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{a.alt_text || a.filename}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>{formatSize(a.size_bytes)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination && pagination.pages > 1 && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'center', gap: 4, flexShrink: 0 }}>
            {[...Array(Math.min(pagination.pages, 6))].map((_, i) => {
              const p = i + 1;
              return (
                <button key={p} onClick={() => setPage(p)} style={{ width: 28, height: 28, borderRadius: 4, fontSize: 11, cursor: 'pointer', background: p === page ? 'var(--accent)' : 'var(--bg-elevated)', color: p === page ? '#0d0f12' : 'var(--text-secondary)', border: `1px solid ${p === page ? 'var(--accent)' : 'var(--border)'}` }}>{p}</button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Slot card ─────────────────────────────────────────────
function SlotCard({ slot, onUpdate }) {
  const [picking, setPicking] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [manualUrl, setManualUrl] = useState('');
  const [showManual, setShowManual] = useState(false);

  const isVideo = slot.type === 'video';
  const isImage = slot.type === 'image';
  const hasValue = !!slot.url;

  const handlePick = async (asset) => {
    setPicking(false);
    setSaving(true);
    try {
      const res = await fetch(`${API}/${slot.key}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ value: asset.file_url }),
      });
      const data = await res.json();
      onUpdate(slot.key, data.data?.url || asset.file_url);
    } catch { alert('Failed to save slot'); } finally { setSaving(false); }
  };

  const handleManualSave = async () => {
    if (!manualUrl.trim()) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/${slot.key}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ value: manualUrl.trim() }),
      });
      const data = await res.json();
      onUpdate(slot.key, data.data?.url || manualUrl.trim());
      setManualUrl('');
      setShowManual(false);
    } catch { alert('Failed to save slot'); } finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!window.confirm(`Clear "${slot.label}"?`)) return;
    setSaving(true);
    try {
      await fetch(`${API}/${slot.key}`, { method: 'DELETE', headers: authHeaders() });
      onUpdate(slot.key, null);
    } catch { alert('Failed to clear slot'); } finally { setSaving(false); }
  };

  return (
    <div style={{ background: 'var(--bg-surface)', border: `1px solid ${hasValue ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden', transition: 'all 0.15s' }}>

      {/* Preview */}
      <div style={{ height: 140, background: 'var(--bg-elevated)', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
        {hasValue && isImage && (
          <img src={slot.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
        )}
        {hasValue && isVideo && (
          <video src={slot.url} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        )}
        {!hasValue && (
          <div style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 28, marginBottom: 6, opacity: 0.3, color: TYPE_COLOR[slot.type] }}>{TYPE_ICON[slot.type]}</div>
            <div style={{ fontSize: 11 }}>No {slot.type} assigned</div>
          </div>
        )}
        {/* Type badge */}
        <div style={{ position: 'absolute', top: 8, left: 8, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: TYPE_COLOR[slot.type], background: 'var(--bg-surface)', padding: '2px 8px', borderRadius: 4, border: `1px solid ${TYPE_COLOR[slot.type]}40` }}>
          {slot.type}
        </div>
        {hasValue && (
          <div style={{ position: 'absolute', top: 8, right: 8, width: 8, height: 8, borderRadius: '50%', background: 'var(--green-light)', boxShadow: '0 0 6px var(--green-light)' }} />
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>{slot.label}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 10, lineHeight: 1.5 }}>{slot.description}</div>

        {/* Slot key */}
        <div style={{ fontFamily: 'monospace', fontSize: 10, color: 'var(--accent)', background: 'var(--accent-muted)', padding: '2px 8px', borderRadius: 4, display: 'inline-block', marginBottom: 12 }}>{slot.key}</div>

        {/* Current URL */}
        {hasValue && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Current URL</div>
            <div style={{ fontSize: 10, fontFamily: 'monospace', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', background: 'var(--bg-elevated)', padding: '4px 8px', borderRadius: 4 }}>{slot.url}</div>
          </div>
        )}

        {/* Manual URL input */}
        {showManual && (
          <div style={{ marginBottom: 10 }}>
            <input
              value={manualUrl}
              onChange={e => setManualUrl(e.target.value)}
              placeholder="https://... or /uploads/videos/..."
              style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)', color: 'var(--text-primary)', padding: '7px 10px', fontSize: 11, fontFamily: 'monospace', boxSizing: 'border-box' }}
            />
          </div>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {(isVideo || isImage) && (
            <Button size="sm" onClick={() => setPicking(true)} loading={saving} style={{ flex: 1 }}>
              {hasValue ? '⇄ Change' : '+ Assign'}
            </Button>
          )}
          {showManual ? (
            <>
              <Button size="sm" onClick={handleManualSave} loading={saving}>Save URL</Button>
              <Button size="sm" variant="ghost" onClick={() => setShowManual(false)}>Cancel</Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setShowManual(true)}>URL</Button>
          )}
          {hasValue && (
            <Button size="sm" variant="danger" onClick={handleClear} loading={saving}>Clear</Button>
          )}
        </div>
      </div>

      {/* Picker modal */}
      {picking && (
        <MediaPicker
          type={slot.type}
          onPick={handlePick}
          onClose={() => setPicking(false)}
        />
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function MediaSlotsPage() {
  const [slots, setSlots]     = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(API, { headers: { Authorization: `Bearer ${localStorage.getItem('mh_token')}` } })
      .then(r => r.json())
      .then(d => setSlots(d.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpdate = (key, url) => {
    setSlots(prev => prev.map(s => s.key === key ? { ...s, url } : s));
  };

  const videoSlots    = slots.filter(s => s.type === 'video');
  const imageSlots    = slots.filter(s => s.type === 'image');
  const otherSlots    = slots.filter(s => s.type !== 'video' && s.type !== 'image');

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>Loading slots…</div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>

      {/* Header info */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
        <div style={{ fontSize: 24, color: 'var(--accent)' }}>◈</div>
        <div>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>Media Slots</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
            Assign videos and images to named slots used across the public SPA. Changes take effect immediately — no redeploy needed.
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 16, flexShrink: 0 }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--accent)' }}>{slots.filter(s => s.url).length}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, color: 'var(--text-muted)' }}>{slots.filter(s => !s.url).length}</div>
            <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Empty</div>
          </div>
        </div>
      </div>

      {/* Video slots */}
      {videoSlots.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: 'var(--blue-light)' }}>▶</span>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Video Slots</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{videoSlots.filter(s => s.url).length}/{videoSlots.length} assigned</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {videoSlots.map(s => <SlotCard key={s.key} slot={s} onUpdate={handleUpdate} />)}
          </div>
        </div>
      )}

      {/* Image slots */}
      {imageSlots.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: 'var(--accent)' }}>◈</span>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Image Slots</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{imageSlots.filter(s => s.url).length}/{imageSlots.length} assigned</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {imageSlots.map(s => <SlotCard key={s.key} slot={s} onUpdate={handleUpdate} />)}
          </div>
        </div>
      )}

      {/* Other slots */}
      {otherSlots.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 14, color: 'var(--green-light)' }}>◻</span>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14 }}>Other Settings</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {otherSlots.map(s => <SlotCard key={s.key} slot={s} onUpdate={handleUpdate} />)}
          </div>
        </div>
      )}
    </div>
  );
}
